import React, { useState } from 'react'
import { Grid, Checkbox, Tooltip, Link } from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { Formik, Field } from 'formik'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import StyledRadio from '@src/routes/Configurators/utils/ConfiguratorCustomRadioButton'
import RadioGroup from '@material-ui/core/RadioGroup'
import LiquidSelectAdapter from '@src/components/LiquidSelectAdapter'
import configMessages from '../../../messages'
import UtilMessages from '@utils/messages'
import { configContext } from '../../../utils/StandardReducer'
import { FormattedMessage, useIntl } from 'react-intl'
import ModifiedSpecPopUp from '@src/routes/Configurators/components/PopUp/ModifiedSpecPopUp'
import RecommendationPopUp from '@src/routes/Configurators/components/PopUp/RecommendationLink'
import ValidationErrors from '../../../validations/ValidationErrors'
import ExpectedYields from './ExpectedYields'
import ODYieldPopup from '../../../../Configurators/components/PopUp/ODYieldPopup'
import {
  TubeProperties,
  TubeProduct,
  Modification,
  Purification,
  CountryType,
  UploadType,
} from '../../../utils/StandardMTOEnum'
import ValidationWarnings from '../../../validations/ValidationWarnings'
import InfoIcon from '@material-ui/icons/Info'
import styles from '@src/routes/Configurators/utils/styles'
import LiquidInputAdapter from '@src/components/LiquidInputAdapter'
import { checkValidKey } from '@src/routes/Configurators/utils/util'
import { cloneDeep } from 'lodash'
import clsx from 'clsx'

const useStyles = makeStyles((theme: Theme) => {
  return {
    contentWrapper: {
      backgroundColor: theme.palette.common.white,
      padding: theme.spacing(2, 5, 4),
      marginBottom: theme.spacing(5),
    },
    subHead: {
      fontSize: theme.typography.pxToRem(16),
      fontWeight: theme.typography.fontWeightMedium,
      margin: theme.spacing(2, 0),
      marginBottom: theme.spacing(4),
    },
    label: {
      fontWeight: theme.typography.fontWeightMedium,
      fontSize: theme.typography.pxToRem(14),
      marginBottom: theme.spacing(2),
    },
    labelForTubes: {
      fontWeight: theme.typography.fontWeightMedium,
      fontSize: theme.typography.pxToRem(14),
      marginBottom: theme.spacing(2),
      marginTop: theme.spacing(5),
    },
    specificationsWrapper: {
      '& span.MuiFormControlLabel-label': {
        fontSize: theme.typography.pxToRem(14),
      },
    },
    icon: {
      fontSize: theme.typography.pxToRem(14),
      verticalAlign: 'middle',
    },
    radio: {
      padding: theme.spacing(1),
      marginLeft: theme.spacing(0.5),
    },
    fieldLabel: {
      '& label': {
        fontWeight: theme.typography.fontWeightMedium,
        fontSize: theme.typography.pxToRem(14),
      },
      '& .MuiInput-root': {
        width: '75%',
        '& .MuiSelect-root': {
          height: 35,
          lineHeight: '25px',
        },
      },
    },
    minYield: {
      textAlign: 'center',
      fontStyle: 'italic',
    },
    applyAll: {
      marginTop: theme.spacing(-2),
      textAlign: 'right',
    },
    InfoIcon: {
      outlined: true,
      position: 'relative',
      fontSize: theme.typography.pxToRem(16),
      top: theme.spacing(0.5),
      marginLeft: theme.spacing(1.5),
    },
    InfoPopup: {
      maxWidth: 270,
      backgroundColor: theme.palette.common.white,
      position: 'relative',
      boxShadow: theme.shadows[4],
      padding: theme.spacing(5),
      fontSize: theme.typography.pxToRem(12),
      color: theme.palette.common.black,
      borderRadius: '2%',
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    arrow1: {
      color: theme.palette.common.white,
    },
    titleText: {
      fontWeight: 'normal',
    },
    arrow: {
      color: theme.palette.common.white,
    },
    iconHead: {
      fontSize: theme.typography.pxToRem(16),
      verticalAlign: 'middle',
    },
    largeAmount: {
      fontWeight: theme.typography.fontWeightMedium,
      color: theme.palette.primary.main,
    },
    radioLevel: {
      height: theme.spacing(5.5),
    },
    radioLevelForCpeptide: {
      marginTop: theme.spacing(3.0),
    },
    purificationMargin: {
      height: theme.spacing(6.5),
    },
    mgBottom: {
      marginBottom: theme.spacing(3),
    },
    widthFillContent: {
      width: 'fit-content',
      display: 'inline-table',
    },
    disableText: {
      backgroundColor: theme.palette.common.white,
      padding: theme.spacing(2, 5, 4),
      marginBottom: theme.spacing(5),
      color: theme.palette.text.disabled,
      '& .MuiTypography-colorPrimary': {
        color: theme.palette.text.disabled,
      },
      '&  .MuiSvgIcon-colorSecondary': {
        color: theme.palette.text.disabled,
      },
    },
    dropDownDisable: {
      '& .MuiSelect-selectMenu:focus': {
        borderColor: theme.palette.background.grey,
      },
    },
    dropDownEnable: {
      '& .MuiSelect-selectMenu:focus': {
        borderColor: theme.palette.vibrant.cyan,
      },
    },
    fontBold: {
      fontWeight: theme.typography.fontWeightBold,
    },
  }
})

const Specifications = (props) => {
  const classes = useStyles()
  const intl = useIntl()
  const GlobalCSS = styles()
  const messages = { ...configMessages, ...UtilMessages }
  const { specificationsData, productDetails, noOfTubes, setNoOfTubes } = props
  const tubes =
    specificationsData.specifications !== undefined &&
    specificationsData.specifications.filter(
      (spec) => spec.categoryName === 'tubes'
    )
  const tubeModifications =
    tubes !== undefined && tubes.map((tube) => tube.specificationModifications)
  let tubesList: any = []
  tubesList = tubeModifications[0]
  const nonTubes =
    specificationsData.specifications !== undefined &&
    specificationsData.specifications.filter(
      (spec) =>
        spec.categoryName !== 'tubes' && spec.categoryName !== 'probeType'
    )
  if (
    productDetails.isCSRSupervisor &&
    productDetails.productType === TubeProduct.Standard
  ) {
    nonTubes.forEach((element) => {
      if (element.categoryDisplayName === '_PC_SPEC_CONCENTRATION') {
        const isContainsOther = element.specificationModifications.some(
          (item) => item.displayName === '_PC_SPEC_OTHER'
        )
        // For CSR user we have to display other option in concentration
        const OthersObj = {
          name: 'Other',
          displayName: '_PC_SPEC_OTHER',
          value: 'OTHER',
          __typename: 'SpecificationModifications',
        }
        if (!isContainsOther) {
          element.specificationModifications.push(OthersObj)
        }
      }
    })
  }
  let errorValidation = ['']
  let tubeErrorValidation = ['']
  const { state, dispatch } = React.useContext(configContext)
  const [specificationCategory, setSpecificationCategory] = useState<string>('')
  const {
    sessionData,
    currentOligoIndex,
    isSequenceValid,
    resetValidation,
    errorResponseJson,
    isTubePageLoad,
    specificationWarningMsg,
  } = state

  const [dialogOpen, setDialogOpen] = useState(false)
  const [isTubeError, setIsTubeError] = useState(false)
  const [minimumYield, setMinimumYield] = useState(0)
  const [applyAll, setApplyAll] = React.useState({
    checked: false,
  })
  const [isScaleclicked, setIsScaleClicked] = useState(false)
  const [isPurificationclicked, setIsPurificationClicked] = useState(false)
  const [othersValue, setOthersValue] = React.useState(
    sessionData[currentOligoIndex].otherValue
  )
  if (
    errorResponseJson &&
    Object.keys(state.errorResponseJson).length !== 0 &&
    errorResponseJson.listTubes &&
    !sessionData[currentOligoIndex].isOverridden
  ) {
    const listData = tubesList
    tubesList = []

    errorResponseJson.listTubes.forEach((element, index) => {
      if (listData.length !== 0 && listData[index]?.value === element) {
        tubesList.push(listData[index])
      }
    })
  }
  const sortServices = (services, categoryName) => {
    const sortedData: any = []
    const additionalservices =
      specificationsData.specifications !== undefined &&
      specificationsData.specifications.filter(
        (spec) => spec.categoryName === categoryName
      )[0].specificationModifications

    additionalservices.forEach((data) => {
      if (services.includes(data.value)) {
        sortedData.push(data.value)
      }
    })

    return sortedData
  }
  const onSpecificationChanged = (event, categoryName) => {
    const { name, value } = event.target
    dispatch({ type: 'SPECIFICATION_TOTAL_VALIDATION', data: false })
    if (name === TubeProperties.Scale || name === TubeProperties.Amount) {
      dispatch({ type: 'IS_AMOUNT_CHANGED', data: true })
      setIsScaleClicked(true)
    } else {
      setIsScaleClicked(false)
    }
    if (name === TubeProperties.Format) {
      dispatch({ type: 'IS_FORMAT_CHANGED', data: true })
    }
    if (currentOligoIndex !== 0) {
      setApplyAll({ ...applyAll, checked: false })
      sessionData.forEach((data) => {
        data.applyToAll = false
      })
    }

    if (minimumYield !== 0) {
      sessionData[currentOligoIndex].minimumYield = minimumYield.toString()
    }

    if (productDetails.productType === TubeProduct.Rna) {
      if (sessionData[currentOligoIndex].fiveMod === '') {
        sessionData[currentOligoIndex].fiveMod = Modification.DefaultRnaMod
      }
      if (sessionData[currentOligoIndex].threeMod === '') {
        sessionData[currentOligoIndex].threeMod = Modification.DefaultRnaMod
      }
    }
    if (productDetails.productType === TubeProduct.LongOligo) {
      if (value === Purification.Cartridge) {
        sessionData[currentOligoIndex].amount = Purification.FiveAmount
      } else {
        sessionData[currentOligoIndex].amount = Purification.PointFiveAmount
      }
    }
    dispatch({ type: 'IS_CONFIRM_ORDER', data: false })
    dispatch({ type: 'SET_ERROR_ICON', data: false })

    if (
      name === messages.CONF_FORMAT_TEXT.defaultMessage &&
      value === messages._PC_SPEC_DRY.defaultMessage.toUpperCase()
    ) {
      sessionData[currentOligoIndex].concentration = ''
      state.errorData.concentration = ''
      sessionData[currentOligoIndex].otherValue = ''
      state.errorData.otherValue = ''
      setOthersValue('')
      dispatch({
        type: 'IS_CONCENTRATION_ENABLE',
        data: false,
      })
      dispatch({
        type: 'UPDATE_INPUT',
        data: [...state.sessionData],
      })
      dispatch({
        type: 'UPDATE_ERROR_DATA',
        data: { ...state.errorData },
      })
    }
    if (name === TubeProperties.Conjugation && value === TubeProperties.None) {
      sessionData[currentOligoIndex].conjugationAmount = ''
      sessionData[currentOligoIndex].conjugationChemistry = ''
      sessionData[currentOligoIndex].additionalCarrierProtein =
        TubeProperties.None
      dispatch({
        type: 'UPDATE_ERROR_DATA',
        data: {
          ...state.errorData,
          conjugationAmount: '',
          conjugationChemistry: '',
          additionalCarrierProtein: TubeProperties.None,
        },
      })

      dispatch({
        type: 'UPDATE_INPUT',
        data: [...state.sessionData],
      })
    }
    if (
      productDetails.productType === TubeProduct.iscaledna &&
      name === TubeProperties.Purification
    ) {
      // Making salt as a Default selection for any purification selected in iscaledna
      const services: any =
        cloneDeep(state.sessionData[currentOligoIndex].additionalServices) || []
      if (!services.includes(TubeProperties.SALT)) {
        services.push(TubeProperties.SALT)
        if (
          value === TubeProperties.VIVO &&
          !services.includes(TubeProperties.FILTER)
        ) {
          services.push(TubeProperties.FILTER)
        }
        state.sessionData[currentOligoIndex].additionalServices = services
      }
      state.sessionData[currentOligoIndex].flag = 0
      setIsPurificationClicked(true)
    }
    if (
      productDetails.productType === TubeProduct.iscaledna &&
      name === TubeProperties.AdditionalServices
    ) {
      let services: any = cloneDeep(
        state.sessionData[currentOligoIndex].additionalServices
      )
      if (!services) {
        services = []
      }
      const index = services.indexOf(value)
      if (index !== -1) {
        services.splice(index, 1)
      } else {
        services.push(value)
        services = sortServices(services, categoryName)
      }
      state.sessionData[currentOligoIndex].additionalServices = services
      state.sessionData[currentOligoIndex].flag = 1
    } else {
      sessionData[currentOligoIndex][name] = value
    }
    if (sessionData.length > 1 && applyAll.checked && currentOligoIndex === 0) {
      sessionData.forEach((data) => {
        data.scale = sessionData[currentOligoIndex].scale
        data.amount = sessionData[currentOligoIndex].amount
        data.purification = sessionData[currentOligoIndex].purification
        data.concentration = sessionData[currentOligoIndex].concentration
        data.format = sessionData[currentOligoIndex].format
        data.tubes = sessionData[currentOligoIndex].tubes
        data.conjugation = sessionData[currentOligoIndex].conjugation
        data.conjugationAmount =
          sessionData[currentOligoIndex].conjugationAmount
        data.conjugationChemistry =
          sessionData[currentOligoIndex].conjugationChemistry
        data.additionalCarrierProtein =
          sessionData[currentOligoIndex].additionalCarrierProtein
        data.purityLevel = sessionData[currentOligoIndex].purityLevel
        data.additionalServices =
          sessionData[currentOligoIndex].additionalServices
        data.flag = sessionData[currentOligoIndex].flag
      })
    }
    dispatch({
      type: 'UPDATE_INPUT',
      data: [...state.sessionData],
    })
    if (sessionData.length > 1 && applyAll.checked && currentOligoIndex === 0) {
      dispatch({ type: 'UPDATE_APPLY_TO_ALL_TOTAL_VALIDATION', data: true })
    }
    if (
      productDetails.productType === TubeProduct.iscaledna &&
      name === TubeProperties.AdditionalServices
    ) {
      dispatch({
        type: 'UPDATE_ERROR_DATA',
        data: {
          ...state.errorData,
          additionalServices: sessionData[currentOligoIndex].additionalServices,
          flag: sessionData[currentOligoIndex].flag,
        },
      })
    } else {
      if (
        productDetails.productType === TubeProduct.iscaledna &&
        name === TubeProperties.Purification
      ) {
        dispatch({
          type: 'UPDATE_ERROR_DATA',
          data: { ...state.errorData, [name]: value, flag: 0 },
        })
      } else {
        dispatch({
          type: 'UPDATE_ERROR_DATA',
          data: { ...state.errorData, [name]: value },
        })
      }
    }
    setSpecificationCategory(categoryName)

    return true
  }

  const setDefaultValue = () => {
    const oldPurification = state.errorData.purification
    if (
      state.errorResponseJson.listPurification &&
      state.errorResponseJson.listPurification.length === 1 &&
      state.errorResponseJson.listPurification[0] !== TubeProperties.None
    ) {
      sessionData[currentOligoIndex].purification =
        state.errorResponseJson.listPurification[0]
      state.errorData.purification = state.errorResponseJson.listPurification[0]
      setIsPurificationClicked(true)
    }

    if (
      sessionData[currentOligoIndex]?.format?.length === 0 &&
      state.errorResponseJson.listFormat &&
      state.errorResponseJson.listFormat[0] !== TubeProperties.None &&
      state.errorResponseJson.listFormat.length === 1
    ) {
      sessionData[currentOligoIndex].format =
        state.errorResponseJson.listFormat[0]
      if (
        state.errorResponseJson.listFormat[0] ===
        messages._PC_SPEC_DRY.defaultMessage.toUpperCase()
      ) {
        sessionData[currentOligoIndex].concentration = ''
        state.errorData.concentration = ''
        sessionData[currentOligoIndex].otherValue = ''
        state.errorData.otherValue = ''
        setOthersValue('')
        dispatch({
          type: 'IS_CONCENTRATION_ENABLE',
          data: false,
        })
      }
      state.errorData.format = state.errorResponseJson.listFormat[0]
    }
    if (oldPurification !== state.errorData.purification) {
      if (
        sessionData.length > 1 &&
        applyAll.checked &&
        currentOligoIndex === 0
      ) {
        sessionData.forEach((data) => {
          data.purification = state.errorData.purification
        })
      }
      dispatch({
        type: 'UPDATE_INPUT',
        data: [...state.sessionData],
      })
      dispatch({
        type: 'UPDATE_ERROR_DATA',
        data: { ...state.errorData },
      })
    }
  }

  const setDefaultAdditionalServices = () => {
    if (state.errorResponseJson.additionalServices) {
      sessionData[currentOligoIndex].additionalServices =
        state.errorResponseJson.additionalServices
      state.errorData.additionalServices =
        state.errorResponseJson.additionalServices
    }
    setIsPurificationClicked(false)
    dispatch({
      type: 'UPDATE_INPUT',
      data: [...state.sessionData],
    })
    dispatch({
      type: 'UPDATE_ERROR_DATA',
      data: { ...state.errorData },
    })
  }

  const validateData = (name) => {
    let flag = false
    if (Object.keys(state.errorResponseJson).length !== 0) {
      errorValidation = []
      tubeErrorValidation = []
      let tubeError = false
      state.errorResponseJson?.listErrors?.filter((item) => {
        if (isTubePageLoad) {
          if (
            item.key === UploadType.prodType &&
            !(
              item.value === TubeProperties.RequiredField ||
              item.value === TubeProperties.RequiredFieldError
            )
          ) {
            tubeErrorValidation.push(item.value)

            tubeError = true
            setIsTubeError(tubeError)
          } else if (
            item.key === name &&
            item.key === TubeProperties.Purification &&
            state.isAmountChanged
          ) {
            if (state.errorResponseJson?.listPurification?.length !== 1) {
              errorValidation.push(item.value)
              flag = true
            }
          } else if (
            item.key === name &&
            item.key === TubeProperties.Concentration &&
            state.isFormatChanged &&
            sessionData[currentOligoIndex].format !== TubeProperties.DRY
          ) {
            if (
              state.isConcentrationEnable &&
              (item.value === TubeProperties.RequiredField ||
                item.value === TubeProperties.RequiredFieldError)
            ) {
              errorValidation.push(item.value)
              flag = true
            }
          } else if (item.key === name) {
            errorValidation.push(item.value)
            flag = true
          }
        } else {
          if (state.isComingFromReviewPage && sessionData.length !== 0) {
            if (
              item.key === UploadType.prodType &&
              !(
                item.value === TubeProperties.RequiredField ||
                item.value === TubeProperties.RequiredFieldError
              )
            ) {
              tubeErrorValidation.push(item.value)

              tubeError = true
              setIsTubeError(tubeError)
            } else if (
              item.key === name &&
              item.key === TubeProperties.Purification &&
              state.isAmountChanged
            ) {
              if (
                state.errorResponseJson?.listPurification?.length !== 1 &&
                !(
                  item.value === TubeProperties.RequiredField ||
                  item.value === TubeProperties.RequiredFieldError
                )
              ) {
                errorValidation.push(item.value)
                flag = true
              }
            } else if (
              item.key === name &&
              item.key === TubeProperties.Concentration &&
              state.isFormatChanged &&
              sessionData[currentOligoIndex].format !== TubeProperties.DRY
            ) {
              if (
                state.isConcentrationEnable &&
                (item.value === TubeProperties.RequiredField ||
                  item.value === TubeProperties.RequiredFieldError)
              ) {
                errorValidation.push(item.value)
                flag = true
              }
            } else if (item.key === name) {
              errorValidation.push(item.value)
              flag = true
            }
          } else {
            if (
              item.key === UploadType.prodType &&
              !(
                item.value === TubeProperties.RequiredField ||
                item.value === TubeProperties.RequiredFieldError
              )
            ) {
              tubeErrorValidation.push(item.value)

              tubeError = true
              setIsTubeError(tubeError)
            } else if (
              item.key === name &&
              item.key === TubeProperties.Purification &&
              state.isAmountChanged
            ) {
              if (
                state.errorResponseJson?.listPurification?.length !== 1 &&
                !(
                  item.value === TubeProperties.RequiredField ||
                  item.value === TubeProperties.RequiredFieldError
                )
              ) {
                errorValidation.push(item.value)
                flag = true
              }
            } else if (
              item.key === name &&
              (item.key === TubeProperties.Purification ||
                item.key === TubeProperties.Scale) &&
              item.value !== TubeProperties.RequiredField
            ) {
              if (item.value !== TubeProperties.RequiredFieldError) {
                errorValidation.push(item.value)
                flag = true
              }
            } else if (
              item.key === name &&
              item.key === TubeProperties.Format &&
              item.value !== TubeProperties.RequiredField &&
              item.value !== TubeProperties.RequiredFieldError &&
              state.isFormatChanged
            ) {
              errorValidation.push(item.value)
              flag = true
            }
          }
        }

        // when user enter inside others textbox that time we have to remove Required field error message from concentration. This needs to be handled from UI thats why  added concentration and othersValue check
        if (
          name === TubeProperties.Concentration &&
          othersValue !== '' &&
          sessionData[currentOligoIndex].concentration === TubeProperties.OTHER
        ) {
          errorValidation = []
        }
        return flag
      })
    } else {
      errorValidation = []
      return false
    }
    return flag
  }

  const validateTotalData = (name) => {
    let flag = false
    errorValidation = []
    tubeErrorValidation = []
    let listErrors: any
    if (state.totalValidationJSON[currentOligoIndex] === undefined) {
      return false
    }

    if (state.totalValidationJSON[currentOligoIndex].listErrors)
      listErrors = state.totalValidationJSON[currentOligoIndex].listErrors
    else listErrors = state.totalValidation[currentOligoIndex]

    if (listErrors !== undefined)
      listErrors.forEach((item: any) => {
        if (item.key === name) {
          errorValidation.push(item.value)
          flag = true
        }
        if (item.key === UploadType.prodType) {
          tubeErrorValidation.push(item.value)
          flag = true
        }
        return flag
      })
    return flag
  }

  const disablingRadioButton = (data, categoryName) => {
    if (
      sessionData[currentOligoIndex].format ===
        messages._PC_SPEC_DRY.defaultMessage.toUpperCase() &&
      categoryName === TubeProperties.Concentration
    ) {
      return true
    }
    //conjugation amount validation needs to be there in override scenario.
    if (
      sessionData[currentOligoIndex].isOverridden &&
      !(
        productDetails.productType === TubeProduct.Cpeptide &&
        categoryName === TubeProperties.ConjugationAmount
      )
    ) {
      return false
    }
    if (
      state.seqLength === 0 ||
      state.seqLength === undefined ||
      !isSequenceValid ||
      data === null
    ) {
      return true
    }

    if (
      sessionData[currentOligoIndex].format === '' &&
      categoryName === TubeProperties.Concentration
    ) {
      return true
    }

    if (
      sessionData[currentOligoIndex].conjugation === TubeProperties.None &&
      categoryName === TubeProperties.ConjugationAmount
    ) {
      return true
    }
    if (
      sessionData[currentOligoIndex].conjugation === TubeProperties.None &&
      categoryName === TubeProperties.ConjugationChemistry
    ) {
      return true
    }
    if (
      sessionData[currentOligoIndex].conjugation === TubeProperties.None &&
      categoryName === TubeProperties.additionalCarrierProtein
    ) {
      return true
    }
    if (
      (productDetails.productType === TubeProduct.Standard ||
        productDetails.productType === TubeProduct.Rna) &&
      sessionData[currentOligoIndex].scale === '' &&
      categoryName === TubeProperties.Purification
    ) {
      return true
    }
    if (
      (productDetails.productType === TubeProduct.Wellred ||
        productDetails.productType === TubeProduct.iscaledna) &&
      sessionData[currentOligoIndex].amount === '' &&
      categoryName === TubeProperties.Purification
    ) {
      return true
    }
    if (
      productDetails.productType === TubeProduct.iscaledna &&
      sessionData[currentOligoIndex].purification === '' &&
      categoryName === TubeProperties.AdditionalServices
    ) {
      return true
    }
    if (Object.keys(state.errorResponseJson).length !== 0) {
      let list: any = []
      switch (categoryName) {
        case TubeProperties.Scale: {
          list = state.errorResponseJson.listScale

          break
        }
        case TubeProperties.Concentration: {
          list = state.errorResponseJson.listConcentration

          break
        }
        case TubeProperties.Format: {
          list = state.errorResponseJson.listFormat

          break
        }
        case TubeProperties.Purification: {
          list = state.errorResponseJson.listPurification

          break
        }
        case TubeProperties.PurityLevel: {
          list = state.errorResponseJson.listPurityLevel

          break
        }
        case TubeProperties.Conjugation: {
          list = state.errorResponseJson.listConjugation

          break
        }
        case TubeProperties.ConjugationAmount: {
          list = state.errorResponseJson.listConjugationAmount

          break
        }
        case TubeProperties.Amount: {
          list = state.errorResponseJson.listAmount

          break
        }
        case TubeProperties.AdditionalServices: {
          list = state.errorResponseJson.listAdditionalServices

          break
        }

        default:
          return false
      }
      if (list === null) {
        return true
      } else if (list.includes(data.value)) {
        return false
      }
    }
    return true
  }

  const callValidation = (name) => {
    //conjugation amount validation needs to be there in override scenario.
    if (
      sessionData[currentOligoIndex].isOverridden &&
      !(
        productDetails.productType === TubeProduct.Cpeptide &&
        name === TubeProperties.ConjugationAmount
      )
    ) {
      return false
    }
    if (state.specificationTotalValidation) {
      return validateTotalData(name)
    } else {
      return validateData(name)
    }
  }
  const callValidationBeacon = (name) => {
    if (sessionData[currentOligoIndex].isOverridden) {
      return false
    }
    if (
      state.specificationTotalValidation &&
      name !== TubeProperties.Purification
    ) {
      return validateTotalData(name)
    } else {
      return validateData(name)
    }
  }
  const isFieldDisable = () => {
    if (sessionData[currentOligoIndex].isOverridden) {
      return false
    }
    return !isSequenceValid
  }

  const isPhosphorothioatesSelected = () => {
    return sessionData[currentOligoIndex] &&
      (sessionData[currentOligoIndex].fullSOligoBackbone === true ||
        sessionData[currentOligoIndex].sequence?.includes('*'))
      ? true
      : false
  }

  const handleOtherValue = () => {
    dispatch({ type: 'SPECIFICATION_TOTAL_VALIDATION', data: false })
    dispatch({ type: 'IS_TUBE_PAGE_LOAD', data: true })
    dispatch({ type: 'SET_ERROR_ICON', data: false })
    sessionData[currentOligoIndex].otherValue = othersValue
    state.errorData.otherValue = othersValue
    dispatch({
      type: 'UPDATE_INPUT',
      data: [...state.sessionData],
    })
    dispatch({
      type: 'UPDATE_ERROR_DATA',
      data: { ...state.errorData },
    })
  }
  const handleOtherOption = (e) => {
    setOthersValue(e.target.value)
    dispatch({ type: 'IS_TUBE_PAGE_LOAD', data: true })
  }

  const handleServiceOnchange = (event, categoryName) => {
    const { value } = event.target
    const services: any =
      cloneDeep(state.sessionData[currentOligoIndex].additionalServices) || []

    const index = services.indexOf(value)
    if (
      !(index !== -1 && value === TubeProperties.SALT) &&
      !(
        index !== -1 &&
        value === TubeProperties.FILTER &&
        sessionData[currentOligoIndex].purification === TubeProperties.VIVO
      )
    ) {
      onSpecificationChanged(event, categoryName)
    }
  }

  const renderModifications = (modifications, categoryName) => {
    if (modifications.length > 1) {
      return modifications.map((modification, index: number) => {
        return (
          <>
            <div>
              {categoryName === TubeProperties.Concentration &&
              productDetails.isCSRSupervisor &&
              productDetails.productType === TubeProduct.Standard &&
              modification === modifications[modifications.length - 1] ? (
                <Grid container>
                  <Grid item sm={6} xs={6}>
                    <FormControlLabel
                      className={classes.widthFillContent}
                      value={modification.value}
                      control={<StyledRadio isConfiguratorsRedesign="true" />}
                      label={intl.formatMessage(messages._PC_SPEC_OTHER)}
                      disabled={disablingRadioButton(
                        modification,
                        categoryName
                      )}
                      key={index}
                    />
                  </Grid>
                  <Grid item sm={6} xs={6}>
                    <Field
                      name="othersValue"
                      component={LiquidInputAdapter}
                      value={othersValue}
                      onBlur={() => {
                        handleOtherValue()
                      }}
                      onChange={handleOtherOption}
                      disabled={disablingRadioButton(
                        modification,
                        categoryName
                      )}
                      filled
                      autoComplete="off"
                      className={GlobalCSS.textInputOthers}
                    />
                  </Grid>
                </Grid>
              ) : productDetails.productType === TubeProduct.iscaledna &&
                categoryName === TubeProperties.AdditionalServices ? (
                ''
              ) : (
                <FormControlLabel
                  className={classes.widthFillContent}
                  value={modification.value}
                  checked={
                    modification.value ===
                    state.sessionData[state.currentOligoIndex][categoryName]
                  }
                  control={<StyledRadio isConfiguratorsRedesign="true" />}
                  label={intl.formatMessage(
                    checkValidKey(
                      messages[
                        modification.displayName.replace(/[^a-zA-Z0-9]/g, '_')
                      ],
                      modification.displayName.replace(/[^a-zA-Z0-9]/g, '_')
                    )
                  )}
                  disabled={disablingRadioButton(modification, categoryName)}
                  key={index}
                />
              )}
            </div>

            <div>
              {productDetails.productType !== TubeProduct.Rna &&
              categoryName === TubeProperties.Scale &&
              modification === modifications[modifications.length - 1] ? (
                isSequenceValid ? (
                  <Link
                    href="/services/support/iscale-oligos-request"
                    underline="hover"
                    target="_blank"
                    className={classes.largeAmount}
                  >
                    <FormattedMessage
                      {...messages.CONF_LARGE_AMOUNT_STANDARD}
                    />
                  </Link>
                ) : (
                  <Link component="button" underline={'none'} disabled>
                    {
                      <FormattedMessage
                        {...messages.CONF_LARGE_AMOUNT_STANDARD}
                      />
                    }
                  </Link>
                )
              ) : (
                ''
              )}
            </div>
          </>
        )
      })
    } else {
      return modifications.map((modification, index: number) => {
        if (categoryName === TubeProperties.Purification) {
          sessionData[currentOligoIndex].purification = Purification.HPLC
          state.errorData.purification = Purification.HPLC
        }
        return (
          <>
            <div key={index}>
              {' '}
              <FormattedMessage
                {...checkValidKey(
                  messages[modification.displayName],
                  modification.displayName
                )}
              />
            </div>
          </>
        )
      })
    }
  }
  const renderSpecificationsFormat = () => {
    return <div>{<FormattedMessage {...messages._PC_SPEC_DRY} />}</div>
  }

  const renderAdditionalServicesCheckBox = (modifications, categoryName) => {
    return modifications.map((modification, index: number) => {
      return (
        <>
          <Grid>
            <FormControlLabel
              classes={{ label: GlobalCSS.fontWeightForServices }}
              className={GlobalCSS.checkedIcon}
              control={
                <Checkbox
                  name="additionalServices"
                  size="small"
                  color="primary"
                  checked={
                    sessionData[currentOligoIndex].additionalServices
                      ? sessionData[
                          currentOligoIndex
                        ].additionalServices.indexOf(modification.value) !== -1
                      : false
                  }
                  value={modification.value}
                  key={index}
                  onChange={(event) =>
                    handleServiceOnchange(event, categoryName)
                  }
                />
              }
              label={intl.formatMessage(
                checkValidKey(
                  messages[
                    modification.displayName.replace(/[^a-zA-Z0-9]/g, '_')
                  ],
                  modification.displayName.replace(/[^a-zA-Z0-9]/g, '_')
                )
              )}
              disabled={disablingRadioButton(modification, categoryName)}
            />
          </Grid>
        </>
      )
    })
  }

  const renderSpecificationServices = (specifications) => {
    if (productDetails.productType === TubeProduct.iscaledna) {
      return specifications.map((specification, index) => {
        if (specification.categoryName === TubeProperties.AdditionalServices) {
          return (
            <>
              <div key={index} className={classes.label}>
                <FormattedMessage
                  {...messages[`${specification.categoryDisplayName}`]}
                />
              </div>
              {renderAdditionalServicesCheckBox(
                specification.specificationModifications,
                specification.categoryName
              )}
            </>
          )
        }
        return null
      })
    }
  }

  const callTooltip = (toolTipMsg) => {
    return (
      <>
        <Tooltip
          title={toolTipMsg}
          placement="bottom-end"
          classes={{
            tooltip: classes.InfoPopup,
            arrow: classes.arrow1,
          }}
          arrow
          disableHoverListener={isFieldDisable()}
        >
          <InfoIcon
            aria-label="info"
            className={classes.InfoIcon}
            color="primary"
          ></InfoIcon>
        </Tooltip>
      </>
    )
  }

  const renderSpecifications = (specifications) => {
    if (
      productDetails.productType === TubeProduct.Wellred ||
      productDetails.productType === TubeProduct.iscaledna ||
      productDetails.productType === TubeProduct.LongOligo
    ) {
      const isLongOligo = productDetails.productType === TubeProduct.LongOligo
      return specifications.map((specification, index) => {
        return (
          <>
            {productDetails.productType === TubeProduct.LongOligo &&
            index === 0 ? (
              ''
            ) : (
              <Grid item sm={isLongOligo ? 6 : 3} key={index}>
                {specification.categoryName !== TubeProperties.Amount ||
                productDetails.productType === TubeProduct.iscaledna ||
                productDetails.productType === TubeProduct.Wellred ? (
                  <div className={classes.label}>
                    {productDetails.productType === TubeProduct.iscaledna &&
                    specification.categoryName ===
                      TubeProperties.AdditionalServices
                      ? ''
                      : messages[specification.categoryDisplayName]
                          ?.defaultMessage ||
                        specification.categoryDisplayName}{' '}
                    {productDetails.productType === TubeProduct.iscaledna &&
                    specification.categoryName === TubeProperties.Purification
                      ? callTooltip(
                          intl.formatMessage(
                            messages.CONF_ISCALEDNA_PURIFICATION_POPUP_TEXT
                          )
                        )
                      : ''}
                    <Grid>
                      {specification.categoryName === TubeProperties.Scale ? (
                        state.isModificationPopup ? (
                          <ModifiedSpecPopUp
                            isValid={isFieldDisable()}
                            productType={productDetails.productType}
                          />
                        ) : (
                          <ExpectedYields
                            isValid={isFieldDisable()}
                            productType={productDetails.productType}
                            isFullSOligoBackbone={isPhosphorothioatesSelected()}
                          />
                        )
                      ) : (
                        ''
                      )}
                    </Grid>
                  </div>
                ) : (
                  ''
                )}
                {specification.categoryName === TubeProperties.Format ? (
                  renderSpecificationsFormat()
                ) : (
                  <div>
                    {specification.categoryName !== TubeProperties.Amount ||
                    productDetails.productType === TubeProduct.iscaledna ||
                    productDetails.productType === TubeProduct.Wellred ? (
                      <RadioGroup
                        aria-label="scale"
                        name={specification.categoryName}
                        value={
                          state.sessionData[state.currentOligoIndex][
                            specification.categoryName
                          ]
                        }
                        onChange={(event) =>
                          onSpecificationChanged(
                            event,
                            specification.categoryName
                          )
                        }
                      >
                        {renderModifications(
                          specification.specificationModifications,
                          specification.categoryName
                        )}
                      </RadioGroup>
                    ) : (
                      ''
                    )}
                  </div>
                )}
                {productDetails.productType === TubeProduct.iscaledna &&
                specification.categoryName ===
                  TubeProperties.AdditionalServices ? (
                  ''
                ) : (
                  <>
                    {callValidation(specification.categoryName) ? (
                      <div>
                        <ValidationErrors errors={errorValidation} />
                      </div>
                    ) : (
                      ''
                    )}
                  </>
                )}
              </Grid>
            )}
          </>
        )
      })
    } else {
      const listCount = specifications.filter(
        (specification) => specification?.specificationModifications?.length > 0
      ).length
      return specifications.map((specification, index) => {
        const popup =
          specification.categoryName === TubeProperties.Format ? (
            <>
              {productDetails.productType === TubeProduct.Beacon ||
              productDetails.productType === TubeProduct.Cpeptide ||
              productDetails.productType === TubeProduct.Probe ||
              productDetails.productType === TubeProduct.Rna ? (
                ''
              ) : (
                <Tooltip
                  title={
                    <FormattedMessage {...messages.CONF_FORMAT_INFO_standard} />
                  }
                  placement="bottom-end"
                  classes={{
                    tooltip: classes.InfoPopup,
                    arrow: classes.arrow1,
                  }}
                  arrow
                  disableHoverListener={isFieldDisable()}
                  key={index}
                >
                  <InfoIcon
                    aria-label="info"
                    className={classes.InfoIcon}
                    color="primary"
                  ></InfoIcon>
                </Tooltip>
              )}
            </>
          ) : (
            ''
          )
        return (
          <>
            {specification.specificationModifications.length > 0 ? (
              <Grid
                item
                sm={
                  productDetails.productType !== TubeProduct.Cpeptide ||
                  listCount > 5
                    ? 3
                    : true
                }
                key={index}
              >
                <div className={classes.label}>
                  {specification.specificationModifications.length > 0 &&
                    intl.formatMessage(
                      checkValidKey(
                        messages[specification.categoryDisplayName],
                        specification.categoryDisplayName
                      )
                    )}
                  {popup}
                  <Grid>
                    {specification.categoryName === TubeProperties.Amount &&
                    productDetails.productType !== TubeProduct.Cpeptide ? (
                      <ODYieldPopup
                        isValid={isFieldDisable()}
                        dialogOpen={dialogOpen}
                        setDialogOpen={setDialogOpen}
                        productName={productDetails.productType}
                      />
                    ) : (
                      ''
                    )}
                  </Grid>
                  <Grid>
                    {specification.categoryName === TubeProperties.Scale ? (
                      state.isModificationPopup ? (
                        <ModifiedSpecPopUp
                          isValid={isFieldDisable()}
                          productType={productDetails.productType}
                        />
                      ) : (
                        <ExpectedYields
                          isValid={isFieldDisable()}
                          productType={productDetails.productType}
                          isFullSOligoBackbone={isPhosphorothioatesSelected()}
                        />
                      )
                    ) : (
                      ''
                    )}
                  </Grid>
                  <Grid>
                    {specification.categoryName ===
                      TubeProperties.Purification &&
                    TubeProduct.Probe !== productDetails.productType &&
                    TubeProduct.Beacon !== productDetails.productType ? (
                      <RecommendationPopUp
                        isValid={isFieldDisable()}
                        productType={productDetails.productType}
                      />
                    ) : (
                      ''
                    )}

                    {specification.categoryName ===
                      TubeProperties.Purification &&
                    (TubeProduct.Probe === productDetails.productType ||
                      TubeProduct.Beacon === productDetails.productType) ? (
                      <div className={classes.purificationMargin}></div>
                    ) : (
                      ''
                    )}

                    {specification.categoryName === TubeProperties.Format ||
                    specification.categoryName ===
                      TubeProperties.Concentration ? (
                      <div
                        className={
                          productDetails.productType === TubeProduct.Cpeptide
                            ? classes.radioLevelForCpeptide
                            : classes.radioLevel
                        }
                      ></div>
                    ) : (
                      ''
                    )}
                  </Grid>
                </div>
                <div>
                  <RadioGroup
                    aria-label="scale"
                    name={specification.categoryName}
                    value={
                      state.sessionData[state.currentOligoIndex][
                        specification.categoryName
                      ]
                    }
                    onChange={(event) =>
                      onSpecificationChanged(event, specification.categoryName)
                    }
                  >
                    {renderModifications(
                      specification.specificationModifications,
                      specification.categoryName
                    )}
                  </RadioGroup>
                </div>
                {productDetails.productType !== TubeProduct.Beacon ? (
                  <>
                    {callValidation(specification.categoryName) ? (
                      <div>
                        <ValidationErrors errors={errorValidation} />
                      </div>
                    ) : (
                      ''
                    )}
                  </>
                ) : (
                  <>
                    {callValidationBeacon(specification.categoryName) ? (
                      <div>
                        <ValidationErrors errors={errorValidation} />
                      </div>
                    ) : (
                      ''
                    )}
                  </>
                )}
              </Grid>
            ) : (
              ''
            )}
          </>
        )
      })
    }
  }
  const handleTubeDetails = (event) => {
    const { value } = event.target
    dispatch({ type: 'SPECIFICATION_TOTAL_VALIDATION', data: false })
    dispatch({ type: 'SET_ERROR_ICON', data: false })

    if (value) {
      sessionData[currentOligoIndex].tubes = value
      setNoOfTubes(value)
      if (
        errorResponseJson.listMinimumYield &&
        errorResponseJson.listMinimumYield !== 0
      ) {
        const minYield =
          parseFloat(errorResponseJson.listMinimumYield) / parseFloat(value)
        sessionData[currentOligoIndex].minimumYield = minYield
          .toFixed(2)
          .toString()
      }
      if (
        sessionData.length > 1 &&
        applyAll.checked &&
        currentOligoIndex === 0
      ) {
        sessionData.forEach((data) => {
          data.tubes = sessionData[currentOligoIndex].tubes
        })
      }
      if (currentOligoIndex !== 0) {
        setApplyAll({ ...applyAll, checked: false })
        sessionData.forEach((data) => {
          data.applyToAll = false
        })
      }
      dispatch({
        type: 'UPDATE_INPUT',
        data: [...state.sessionData],
      })
      dispatch({
        type: 'UPDATE_ERROR_DATA',
        data: { ...state.errorData, tubes: value },
      })
      dispatch({
        type: 'IS_CONFIRM_ORDER',
        data: false,
      })
    }
  }

  React.useEffect(() => {
    if (
      state.errorResponseJson.listErrors &&
      state.errorResponseJson.listErrors.length === 0 &&
      state.isComingFromReviewPage &&
      productDetails.productType === TubeProduct.LongOligo
    ) {
      if (
        sessionData[currentOligoIndex].purification === Purification.Cartridge
      ) {
        sessionData[currentOligoIndex].amount = Purification.FiveAmount
      } else {
        sessionData[currentOligoIndex].amount = Purification.PointFiveAmount
      }
    }

    if (specificationCategory !== '') {
      validateData(specificationCategory)
    }
    if (
      errorResponseJson.listMinimumYield &&
      errorResponseJson.listMinimumYield !== 0
    ) {
      const minYield =
        parseFloat(errorResponseJson.listMinimumYield) / parseFloat(noOfTubes)
      setMinimumYield(parseFloat(minYield.toFixed(2)))
      sessionData[currentOligoIndex].minimumYield = minYield
        .toFixed(2)
        .toString()
    } else {
      setMinimumYield(0)
    }
    if (errorResponseJson && isScaleclicked) {
      setDefaultValue()
    }

    if (
      errorResponseJson &&
      productDetails.productType === TubeProduct.iscaledna &&
      isPurificationclicked
    ) {
      setDefaultAdditionalServices()
    }
    setIsScaleClicked(false)

    // TODO: need a test before we refactor these dependencies
  }, [state.errorResponseJson])
  const handleApplyToAll = (isChecked, callTotalValidation) => {
    setApplyAll({ ...applyAll, checked: isChecked })
    // now set epmty data here

    sessionData[currentOligoIndex].applyToAll = isChecked

    sessionData.forEach((data) => {
      data.scale = sessionData[0].scale
      data.amount = sessionData[0].amount
      data.purification = sessionData[0].purification
      data.concentration = sessionData[0].concentration
      data.format = sessionData[0].format
      data.tubes = sessionData[0].tubes
      data.conjugation = sessionData[0].conjugation
      data.conjugationAmount = sessionData[0].conjugationAmount
      data.conjugationChemistry = sessionData[0].conjugationChemistry
      data.additionalCarrierProtein = sessionData[0].additionalCarrierProtein
      data.purityLevel = sessionData[0].purityLevel
      data.additionalServices = sessionData[0].additionalServices
      if (!isChecked) {
        data.applyToAll = isChecked
      }
    })
    dispatch({
      type: 'UPDATE_INPUT',
      data: [...state.sessionData],
    })
    dispatch({
      type: 'UPDATE_APPLY_TO_ALL_TOTAL_VALIDATION',
      data: callTotalValidation,
    })
  }

  React.useEffect(() => {
    if (
      state.isPageLoad === 3 &&
      sessionData !== null &&
      sessionData.length !== 0 &&
      sessionData[state.currentOligoIndex] !== null
    ) {
      if (Object.keys(sessionData[state.currentOligoIndex]).length !== 0) {
        const tubes: any = sessionData[state.currentOligoIndex].tubes
        setNoOfTubes(tubes)
        setOthersValue(sessionData[state.currentOligoIndex].otherValue)
        if (
          sessionData[0].applyToAll !== null &&
          sessionData[0].applyToAll !== undefined
        ) {
          setApplyAll({ ...applyAll, checked: sessionData[0].applyToAll })
          if (sessionData[0].applyToAll) {
            handleApplyToAll(sessionData[0].applyToAll, false)
          }
        }
      }

      dispatch({
        type: 'UPDATE_ERROR_DATA',
        data: {
          ...state.errorData,
          amount: sessionData[state.currentOligoIndex].amount,
          scale: sessionData[state.currentOligoIndex].scale,
          purityLevel: sessionData[state.currentOligoIndex].purityLevel,
          purification: sessionData[state.currentOligoIndex].purification,
          format: sessionData[state.currentOligoIndex].format,
          otherValue: sessionData[state.currentOligoIndex].otherValue,
          concentration: sessionData[state.currentOligoIndex].concentration,
          tubes: sessionData[state.currentOligoIndex].tubes,
          additionalServices:
            sessionData[state.currentOligoIndex].additionalServices,
        },
      })
    }
    dispatch({
      type: 'IS_AMOUNT_CHANGED',
      data: false,
    })
    dispatch({
      type: 'IS_FORMAT_CHANGED',
      data: false,
    })
    if (sessionData[currentOligoIndex].format !== TubeProperties.DRY) {
      dispatch({
        type: 'IS_CONCENTRATION_ENABLE',
        data: true,
      })
    }
    // TODO: need a test before we refactor these dependencies
  }, [state.isPageLoad])

  const isValidSpecification = () => {
    let count = false
    if (
      state.errorResponseJson &&
      state.errorResponseJson.listPurification &&
      sessionData[currentOligoIndex].scale !== '' &&
      sessionData[currentOligoIndex].scale !== undefined &&
      state.errorResponseJson.listPurification.length === 0
    ) {
      count = true
    }

    return count
  }
  const resetSpecificationvalidation = () => {
    errorValidation = []
    setOthersValue('')
    dispatch({
      type: 'RESET_SPECIFICATION_ERROR_MSG',
      data: false,
    })
  }
  React.useEffect(() => {
    if (resetValidation) {
      resetSpecificationvalidation()
    }
    // TODO: need a test before we refactor these dependencies
  }, [resetValidation])

  return (
    <div
      className={
        !isSequenceValid ? classes.disableText : classes.contentWrapper
      }
    >
      <Grid container>
        <Grid
          item
          sm={productDetails.productType === TubeProduct.Cpeptide ? 12 : 6}
        >
          <span className={classes.subHead}>
            <FormattedMessage {...messages.CONF_SPECIFICATIONS} />
            {productDetails.productType === TubeProduct.LongOligo ? (
              ''
            ) : (
              <Tooltip
                title={
                  <FormattedMessage
                    {...messages.CONF_SPECIFICATION_POPUP_MSG}
                  />
                }
                classes={{ tooltip: classes.InfoPopup, arrow: classes.arrow }}
                arrow
                placement="bottom-end"
                disableHoverListener={isFieldDisable()}
              >
                <InfoIcon
                  color="primary"
                  aria-label="info"
                  className={classes.InfoIcon}
                ></InfoIcon>
              </Tooltip>
            )}
          </span>

          {productDetails.productType === TubeProduct.Cpeptide ? (
            <>
              <span className={classes.fontBold}>
                {' '}
                <FormattedMessage {...messages.CONF_NOTE} />{' '}
              </span>
              <span>
                {productDetails.countryCode === CountryType.cpeptideJapan ? (
                  <FormattedMessage {...messages.CONF_NOTE_JP_cpeptide} />
                ) : (
                  <FormattedMessage {...messages.CONF_NOTE_cpeptide} />
                )}
              </span>
            </>
          ) : (
            ''
          )}
        </Grid>

        {currentOligoIndex === 0 ? (
          <Grid
            item
            sm={productDetails.productType === TubeProduct.Cpeptide ? 12 : 6}
            className={clsx(classes.applyAll, GlobalCSS.checkedIcon)}
          >
            <Checkbox
              checked={applyAll.checked}
              onChange={(e) => {
                handleApplyToAll(e.target.checked, true)
              }}
              disabled={!isSequenceValid}
              color="primary"
            />
            <span>
              <FormattedMessage {...messages.CONF_APPLY_TO_ALL} />
            </span>
          </Grid>
        ) : (
          ''
        )}
      </Grid>
      <div className={classes.mgBottom}>
        <ValidationWarnings
          warnings={specificationWarningMsg ? specificationWarningMsg : []}
        />
      </div>
      {isValidSpecification() ? (
        <Grid className={GlobalCSS.invalidSpecificationText}>
          <Grid className={GlobalCSS.specsError}>
            <Grid className={GlobalCSS.message}>
              <FormattedMessage
                {...messages.CONF_INVALID_SPECIFICATION_ERROR}
              />
            </Grid>
          </Grid>
        </Grid>
      ) : (
        ''
      )}

      {productDetails.productType !== TubeProduct.LongOligo ? (
        <Formik
          initialValues={{ tubes: 1 }}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              actions.setSubmitting(false)
            }, 1000)
          }}
        >
          {() => (
            <>
              <Grid container className={classes.specificationsWrapper}>
                {renderSpecifications(nonTubes)}
              </Grid>
              {tubes[0].specificationModifications.length !== 0 ? (
                <>
                  <div className={classes.labelForTubes}>
                    {<FormattedMessage {...messages.CONF_TUBES_PER_OLIGO} />}
                    <span className={classes.titleText}>
                      <Tooltip
                        title={
                          <FormattedMessage
                            {...messages.CONF_NO_TUBES_POPUP_MSG}
                          />
                        }
                        classes={{
                          tooltip: classes.InfoPopup,
                          arrow: classes.arrow1,
                        }}
                        arrow
                        placement="bottom-end"
                        disableHoverListener={isFieldDisable()}
                      >
                        <InfoIcon
                          color="primary"
                          className={classes.InfoIcon}
                        ></InfoIcon>
                      </Tooltip>
                    </span>
                  </div>
                  <Grid
                    container
                    className={classes.fieldLabel}
                    alignItems="flex-end"
                  >
                    <Grid item sm={2}>
                      <Field
                        name="tubes"
                        component={LiquidSelectAdapter}
                        disabled={isFieldDisable()}
                        className={
                          isFieldDisable()
                            ? classes.dropDownDisable
                            : classes.dropDownEnable
                        }
                        value={noOfTubes}
                        options={tubesList.map((tube) => ({
                          label: tube.name,
                          value: tube.value,
                        }))}
                        onClick={handleTubeDetails}
                        filled
                        displayEmpty
                        renderValue={(value: any) => {
                          let noOfTubesOptions: {
                            label: string
                            value: string
                          }[] = []
                          noOfTubesOptions = tubesList

                          const option = noOfTubesOptions.find((obj) => {
                            return obj.value === value
                          })
                          return option?.label || noOfTubes
                        }}
                      />
                    </Grid>

                    {productDetails.productType === TubeProduct.Cpeptide ||
                    productDetails.productType === TubeProduct.Wellred ? (
                      ''
                    ) : minimumYield !== 0 ? (
                      <Grid item className={classes.minYield}>
                        <FormattedMessage
                          {...messages.CONF_MIN_YIELD_PER_TUBE}
                        />{' '}
                        {minimumYield} OD
                      </Grid>
                    ) : (
                      ''
                    )}
                  </Grid>
                </>
              ) : (
                ''
              )}
              {isTubeError ? (
                <Grid className={GlobalCSS.width43}>
                  <ValidationErrors errors={tubeErrorValidation} />
                </Grid>
              ) : (
                ''
              )}
              {productDetails.productType === TubeProduct.iscaledna ? (
                <Grid className={GlobalCSS.specificationsWrapperServices}>
                  {renderSpecificationServices(nonTubes)}
                </Grid>
              ) : (
                ''
              )}
            </>
          )}
        </Formik>
      ) : (
        <Grid container className={classes.specificationsWrapper}>
          {renderSpecifications(nonTubes)}
        </Grid>
      )}
    </div>
  )
}
export default Specifications
